"use client";
import * as timeouts from "../../../constants/timeouts.js";
import useFlyout from "../../../hooks/_private/useFlyout.js";
import useIsDismissible from "../../../hooks/_private/useIsDismissible.js";
import useOnClickOutside from "../../../hooks/_private/useOnClickOutside.js";
import useElementId from "../../../hooks/useElementId.js";
import useHotkeys from "../../../hooks/useHotkeys.js";
import useIsomorphicLayoutEffect from "../../../hooks/useIsomorphicLayoutEffect.js";
import useRTL from "../../../hooks/useRTL.js";
import React from "react";
import { trapFocus } from "../../../utilities/a11y.js";
import { checkTransitions } from "../../../utilities/animation.js";
import { debounce } from "../../../utilities/helpers.js";
import { Provider, useFlyoutContext } from "./Flyout.context.js";
const FlyoutRoot = (props) => {
    const { triggerType = "click", onOpen, onClose, children, forcePosition, trapFocusMode, width, disableHideAnimation, contentGap, contentClassName, contentAttributes, position: passedPosition, active: passedActive, id: passedId, instanceRef, } = props;
    const [isRTL] = useRTL();
    const triggerElRef = React.useRef(null);
    const flyoutElRef = React.useRef(null);
    const id = useElementId(passedId);
    const timerRef = React.useRef();
    const releaseFocusRef = React.useRef(null);
    const lockedRef = React.useRef(false);
    const lockedBlurEffects = React.useRef(false);
    const shouldReturnFocusRef = React.useRef(true);
    const flyout = useFlyout(triggerElRef, flyoutElRef, {
        width,
        position: passedPosition,
        defaultActive: passedActive,
        forcePosition,
    });
    const parentFlyoutContext = useFlyoutContext();
    const { status, updatePosition, render, hide, remove } = flyout;
    // Don't create dismissible queue for hover flyout because they close all together on mouseout
    const isDismissible = useIsDismissible(triggerType !== "hover" && status !== "idle", flyoutElRef, triggerElRef);
    const clearTimer = React.useCallback(() => {
        if (timerRef.current)
            clearTimeout(timerRef.current);
    }, [timerRef]);
    /**
     * Component open/close handlers
     * Called from the internal actions
     */
    const handleOpen = React.useCallback(() => {
        const canOpen = !lockedRef.current && status === "idle";
        if (!canOpen)
            return;
        onOpen?.();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [status]);
    const handleClose = React.useCallback((options) => {
        const isLocked = triggerType === "click" && !isDismissible();
        const canClose = !isLocked && status !== "idle";
        if (!canClose)
            return;
        onClose?.();
        if (options?.closeParents) {
            parentFlyoutContext?.handleClose?.();
        }
    }, 
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [status, isDismissible, triggerType]);
    /**
     * Trigger event handlers
     */
    const handleBlur = React.useCallback((e) => {
        const focusedContent = flyoutElRef.current?.contains(e.relatedTarget);
        if (
        // Empty flyouts don't move the focus so they have to be closed on blur
        focusedContent ||
            // Content menu keeps the focus on the original trigger so moving the focus away from it shouldn't close it
            (triggerType === "hover" && trapFocusMode === "content-menu") ||
            // Prevent from closing in case user interacts with items inside content
            lockedBlurEffects.current) {
            return;
        }
        handleClose();
    }, [handleClose, triggerType, trapFocusMode]);
    const handleFocus = React.useCallback(() => {
        handleOpen();
    }, [handleOpen]);
    const handleMouseEnter = React.useCallback(() => {
        clearTimer();
        timerRef.current = setTimeout(handleOpen, timeouts.mouseEnter);
    }, [clearTimer, timerRef, handleOpen]);
    const handleMouseLeave = React.useCallback(() => {
        clearTimer();
        timerRef.current = setTimeout(() => handleClose(), timeouts.mouseLeave);
    }, [clearTimer, timerRef, handleClose]);
    const handleTriggerClick = React.useCallback(() => {
        if (status === "idle") {
            handleOpen();
        }
        else {
            handleClose();
        }
    }, [status, handleOpen, handleClose]);
    const handleContentMouseDown = () => (lockedBlurEffects.current = true);
    const handleContentMouseUp = () => (lockedBlurEffects.current = false);
    /**
     * Control the display based on the props
     */
    useIsomorphicLayoutEffect(() => {
        if (passedActive) {
            render();
            return;
        }
        if (checkTransitions() && !disableHideAnimation) {
            hide();
            // In case transitions are disabled globally - remove from the DOM immediately
        }
        else {
            remove();
        }
    }, [passedActive, render, hide, disableHideAnimation]);
    const handleTransitionEnd = React.useCallback((e) => {
        if (flyoutElRef.current !== e.currentTarget || e.propertyName !== "transform")
            return;
        if (status === "hidden")
            remove();
    }, [remove, status]);
    /**
     * Handle focus trap
     *
     * We release focus on visible change to not wait till animation ends
     * so if we click outside the flyout, it won't focus the trigger
     * after the animation and open it again
     *
     * Trap focus on visible to avoid scroll jumping, position is defined only when flyout is visible
     */
    useIsomorphicLayoutEffect(() => {
        if (status !== "visible" || !flyoutElRef.current)
            return;
        releaseFocusRef.current = trapFocus(flyoutElRef.current, {
            mode: trapFocusMode,
            includeTrigger: triggerType === "hover" && trapFocusMode === "content-menu",
            onNavigateOutside: () => {
                releaseFocusRef.current = null;
                handleClose();
            },
        });
    }, [status, triggerType, handleClose, trapFocusMode]);
    React.useEffect(() => {
        if (!disableHideAnimation && status !== "hidden")
            return;
        if (disableHideAnimation && status !== "idle")
            return;
        if (releaseFocusRef.current) {
            /* Locking the popover to not open it again on trigger focus */
            if (triggerType === "hover") {
                lockedRef.current = true;
                setTimeout(() => {
                    lockedRef.current = false;
                }, 100);
            }
            releaseFocusRef.current({
                withoutFocusReturn: !shouldReturnFocusRef.current,
            });
            releaseFocusRef.current = null;
            shouldReturnFocusRef.current = true;
        }
    }, [status, triggerType, disableHideAnimation]);
    /**
     * Release focus trapping on unmount
     */
    React.useEffect(() => {
        return () => {
            if (releaseFocusRef.current)
                releaseFocusRef.current();
            releaseFocusRef.current = null;
        };
    }, []);
    /**
     * Update position on resize or RTL
     */
    React.useEffect(() => {
        const update = debounce(updatePosition, 10);
        window.addEventListener("resize", update);
        return () => window.removeEventListener("resize", update);
    }, [updatePosition]);
    React.useEffect(() => {
        updatePosition();
    }, [isRTL, updatePosition]);
    /**
     * Imperative methods for controlling Flyout
     */
    React.useImperativeHandle(instanceRef, () => ({
        open: handleOpen,
        close: handleClose,
    }), [handleOpen, handleClose]);
    useHotkeys({ Escape: () => handleClose() }, [handleClose]);
    useOnClickOutside([flyoutElRef, triggerElRef], () => {
        // Clicking outside changes focused element so we don't need to set it back ourselves
        shouldReturnFocusRef.current = false;
        handleClose();
    });
    return (React.createElement(Provider, { value: {
            id,
            flyout,
            width,
            triggerElRef,
            flyoutElRef,
            handleClose,
            handleOpen,
            handleFocus,
            handleBlur,
            handleMouseEnter,
            handleMouseLeave,
            handleTransitionEnd,
            handleClick: handleTriggerClick,
            handleContentMouseDown,
            handleContentMouseUp,
            triggerType,
            trapFocusMode,
            contentGap,
            contentClassName,
            contentAttributes,
        } }, children));
};
export default FlyoutRoot;
